import { useEffect, useRef, useState } from 'react';
import { setReducedMotion } from '@/components/feature/hairline/kernel';
import { mountReconcile } from './reconcileFigure';
import type { FinState } from './reconcileFigure';
import '@/pages/services/components/service-visual.css';
import './fin-reconciliation.css';

/* Trace copy per *actual* visual phase (reported by the figure, never ahead of the animation). */
const TRACE: Record<FinState, { header: string; tag: string; source: string; ledger: string; outcome: string }> = {
  rest: { header: 'Ready / Reconciliation', tag: 'Ready', source: 'Ready to receive', ledger: 'Ready to receive', outcome: 'Awaiting comparison' },
  matching: { header: 'Live / Matching', tag: 'Matching', source: 'Received', ledger: 'Received', outcome: 'Comparing pair' },
  matched: { header: 'Live / Matched', tag: 'Matched', source: 'Received', ledger: 'Received', outcome: 'Matched & recorded' },
  review: { header: 'Live / Human review', tag: 'Review needed', source: 'Received', ledger: 'Received', outcome: 'Exception needs review' },
};

const BUTTONS: [FinState, string][] = [['matching', 'Matching'], ['matched', 'Matched'], ['review', 'Review']];

/* Manual pick holds the chosen state this long before autoplay resumes. */
const RESUME_MS = 6000;

interface Api { choose: (s: FinState) => void }

/* FIN Reconciliation Workbench — ported from FIN_Reconciliation_Workbench v1 (no iframe).
   Autoplay by default; clicking a state previews it, then autoplay resumes. QA ?state= override removed. */
export default function FinReconciliation() {
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const apiRef = useRef<Api | null>(null);
  const [phase, setPhase] = useState<FinState>('rest');

  useEffect(() => {
    const stage = stageRef.current;
    const svg = svgRef.current;
    if (!stage || !svg) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = mountReconcile({ stage, svg, read: { textContent: 'rest' } }, 1);
    let hovered = false;
    let holding = false;
    let timer: number | undefined;

    const sync = () => handle.setAutomatic(!media.matches && !hovered && !holding);
    const onMedia = () => { setReducedMotion(media.matches); sync(); };
    /* Hovering the figure with a mouse pauses the demo so the current comparison can be read. */
    const onEnter = (e: PointerEvent) => { if (e.pointerType === 'mouse') { hovered = true; sync(); } };
    const onLeave = () => { hovered = false; sync(); };

    handle.onState(setPhase);
    onMedia();
    stage.addEventListener('pointerenter', onEnter);
    stage.addEventListener('pointerleave', onLeave);
    media.addEventListener('change', onMedia);

    apiRef.current = {
      choose: (s) => {
        window.clearTimeout(timer);
        holding = true;
        sync();
        handle.setState(s);
        timer = window.setTimeout(() => { holding = false; sync(); }, RESUME_MS);
      },
    };

    return () => {
      apiRef.current = null;
      window.clearTimeout(timer);
      stage.removeEventListener('pointerenter', onEnter);
      stage.removeEventListener('pointerleave', onLeave);
      media.removeEventListener('change', onMedia);
      handle.destroy();
    };
  }, []);

  const t = TRACE[phase];

  return (
    <div className={`sv-root sv-fin state-${phase}`} data-state={phase}>
      <div className="sv-feature">
        <section className="sv-editorial">
          <div>
            <div className="sv-kicker sv-mono"><b>Workflow</b> / Finance operations</div>
            <h3>Two sources.<br />One clear match.</h3>
            <p className="sv-description">Bring source records and ledger entries into alignment. Matched pairs settle together; unmatched items stay visible for human review.</p>
          </div>
          <div className="sv-benefits">
            <div className="sv-benefit"><span className="sv-n">01</span><span>Receive both sources</span></div>
            <div className="sv-benefit"><span className="sv-n">02</span><span>Compare paired records</span></div>
            <div className="sv-benefit"><span className="sv-n">03</span><span>Keep exceptions visible</span></div>
          </div>
        </section>

        <section className="sv-system" aria-label="Interactive reconciliation workbench">
          <header className="sv-system-top">
            <span className="sv-mono sv-kicker">System / Reconciliation</span>
            <span className="sv-live sv-mono"><i />{t.header}</span>
          </header>

          <div ref={stageRef} className="sv-figure-stage fin-stage" data-controlled="true">
            <div className="fin-figure">
              <svg
                ref={svgRef}
                className="fin-svg"
                viewBox="0 60 400 200"
                role="img"
                aria-label="Two source record stacks feed a comparison desk. Aligned pairs join the matched stack; one offset record waits for human review."
              />
              <div className="fin-label fin-source-a">SOURCE RECORDS</div>
              <div className="fin-label fin-source-b">LEDGER ENTRIES</div>
              <div className="fin-label fin-compare">COMPARE</div>
              <div className="fin-label fin-matched">MATCHED</div>
              <div className="fin-label fin-review">REVIEW QUEUE</div>
            </div>
          </div>

          <div className="sv-readout" aria-live="polite">
            <div className="sv-readout-top sv-mono">
              <span>Trace / Representative pair</span>
              <span className="sv-tag">{t.tag}</span>
            </div>
            <div className="sv-events">
              <div className="sv-event"><small>001 / SOURCE RECORDS</small><span>{t.source}</span></div>
              <div className="sv-event"><small>002 / LEDGER ENTRIES</small><span>{t.ledger}</span></div>
              <div className="sv-event"><small>003 / RECONCILIATION</small><span className="sv-event-state">{t.outcome}</span></div>
            </div>
          </div>

          <div className="sv-controls">
            <div className="sv-controls-caption sv-mono">Demo / Match &amp; review</div>
            <div className="sv-button-group" role="group" aria-label="Preview reconciliation states">
              {BUTTONS.map(([s, label]) => (
                <button
                  key={s}
                  type="button"
                  className="sv-mode cursor-target"
                  aria-pressed={phase === s}
                  onClick={() => apiRef.current?.choose(s)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}